import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { ApAgentAction } from "@/accounts-payable/types";
import {
  actionCategoryLabel,
  actionDetail,
  actionOutcomeCopy,
  isNoOpAction,
  isPendingReviewAction,
} from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";
import { ActionStatusGlyph, ConfidenceLabel } from "./shared";

/**
 * An action row's status glyph, in a box the height of the title's 16px line
 * so the 20px glyph centres on it rather than hanging 2px low.
 */
export const ActionRowGlyph = ({ action }: { action: ApAgentAction }) => (
  <Stack
    direction="row"
    align="center"
    justify="center"
    className="h-4 w-5 flex-none"
  >
    <ActionStatusGlyph action={action} outline className="size-5 flex-none" />
  </Stack>
);

/**
 * The top of an action row, shared by the invoice sidebar and the review page:
 * the category as the title with the confidence (pending) on the right, then
 * the target and any outcome. A flag's category says little, so its reason is
 * the title and the category drops to a caption. Only an apply failure reads
 * red (the Automated tab's rule).
 */
export const ActionRowHeader = ({
  action,
  showTarget,
}: {
  action: ApAgentAction;
  /** False where the row shows the target elsewhere (the review page's
   * ServiceTitan card), so it isn't said twice. */
  showTarget: boolean;
}) => {
  const pending = isPendingReviewAction(action);
  const noOp = isNoOpAction(action);
  const detail = actionDetail(action);
  const category = actionCategoryLabel(action);
  const outcome = actionOutcomeCopy(action);
  const target = showTarget && !noOp ? detail : null;

  return (
    <Stack gap="xs" fullWidth>
      <Stack direction="row" justify="between" align="start" gap="md" fullWidth>
        <Typography variant="subtitleW3">{noOp ? detail : category}</Typography>
        {noOp ? (
          <Typography
            variant="captionW1"
            textColor="tertiary"
            className="flex-none whitespace-nowrap"
          >
            {category}
          </Typography>
        ) : (
          pending && (
            <ConfidenceLabel
              value={action.confidence}
              className="flex-none whitespace-nowrap"
            />
          )
        )}
      </Stack>
      {(target || outcome) && (
        <Typography textColor="secondary">
          {target}
          {outcome && (
            <span
              className={cn({
                "text-negative-text": action.status === "apply_failed",
                "text-tertiary-text": action.status !== "apply_failed",
              })}
            >
              {target && " · "}
              {outcome}
            </span>
          )}
        </Typography>
      )}
    </Stack>
  );
};
